import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePoolStore } from '@/stores/poolStore'
import { usePools } from '@/hooks/use-pools'
import { useAccounts } from '@/hooks/use-accounts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Plus,
  Loader2,
  Group,
  Search,
  Copy,
  Check,
  TestTube,
  Star,
  X,
  Users,
  ChevronDown,
  ChevronRight,
  Crown,
  SlidersHorizontal,
} from 'lucide-react'
import { getPoolIcon, PoolIcon, SystemIcon, AccountIcon, forecastPurposeLabel } from '@/lib/entity-icons'
import { formatDate } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { CreatePoolDialog } from '@/components/pools/CreatePoolDialog'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { useIncognito } from '@/hooks/use-incognito'
import { obfuscatePoolName, obfuscateUUID, obfuscateTags } from '@/lib/incognito'

// ── forecast purpose options ──────────────────────────────────────────────────
const FORECAST_PURPOSE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'gcp_supply',     label: 'GCP Supply Forecasting' },
  { value: 'gcp_feed_in',    label: 'GCP Feed-in Forecasting' },
  { value: 'gcp_without_ev', label: 'GCP without EV Forecasting' },
  { value: 'ev',             label: 'EV Forecasting' },
]

// ── control sub-purpose options ───────────────────────────────────────────────
const CONTROL_PURPOSE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'imbalance',        label: 'Imbalance' },
  { value: 'intraday',         label: 'Intraday' },
  { value: 'intraday_cloud',   label: 'Intraday Cloud' },
  { value: 'tou_stacking_poc', label: 'TOU Stacking' },
]

export default function Dashboard() {
  const { data: pools = [], isLoading: loading } = usePools()
  const { toggleFavorite, isFavorite } = usePoolStore()
  const incognito = useIncognito()
  const { can } = usePermissions()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [groupByAccount, setGroupByAccount] = useState(false)

  // ── new filter state ──────────────────────────────────────────────────────
  const [filterOpen, setFilterOpen] = useState(false)
  const [forecastFilters, setForecastFilters] = useState<Set<string>>(new Set())
  const [controlEnabled, setControlEnabled] = useState(false)
  const [purposeFilters, setPurposeFilters] = useState<Set<string>>(new Set())

  const filterRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // When control is disabled, clear sub-purpose selections
  useEffect(() => {
    if (!controlEnabled) setPurposeFilters(new Set())
  }, [controlEnabled])

  const activeFilterCount =
    forecastFilters.size +
    (controlEnabled ? 1 : 0) +
    purposeFilters.size

  const clearAllFilters = () => {
    setForecastFilters(new Set())
    setControlEnabled(false)
    setPurposeFilters(new Set())
  }

  const toggleForecast = (value: string) => {
    setForecastFilters((prev) => {
      const next = new Set(prev)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })
  }

  const togglePurpose = (value: string) => {
    setPurposeFilters((prev) => {
      const next = new Set(prev)
      next.has(value) ? next.delete(value) : next.add(value)
      return next
    })
  }

  // ── per-column counts (only show options that exist in data) ──────────────
  const forecastCounts = pools.reduce<Record<string, number>>((acc, pool) => {
    const forecasts = (pool as any).forecastConfigs as { purpose: string }[] | undefined
    FORECAST_PURPOSE_OPTIONS.forEach(({ value }) => {
      if (forecasts?.some((f) => f.purpose === value)) {
        acc[value] = (acc[value] || 0) + 1
      }
    })
    return acc
  }, {})

  const controlCount = pools.filter(
    (p) => (p as any).controlConfig !== null && (p as any).controlConfig !== undefined
  ).length

  const purposeCounts = pools.reduce<Record<string, number>>((acc, pool) => {
    const p = (pool as any).controlConfig?.purpose
    if (p) acc[p] = (acc[p] || 0) + 1
    return acc
  }, {})

  const activeForecastOptions = FORECAST_PURPOSE_OPTIONS.filter((o) => (forecastCounts[o.value] ?? 0) > 0)
  const activePurposeOptions  = CONTROL_PURPOSE_OPTIONS.filter((o) => (purposeCounts[o.value] ?? 0) > 0)

  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(new Set())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const { toast } = useToast()

  const accountIDs = groupByAccount ? [...new Set(pools.map((p) => p.accountID))] : []
  const accounts = useAccounts(accountIDs)

  const accountCount = new Set(pools.map((p) => p.accountID)).size
  const poolCount = pools.length
  const systemCount = pools.reduce((sum, p) => sum + p.systemCount, 0)

  const handleCopyPoolId = async (e: React.MouseEvent, poolID: string) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(poolID)
      setCopiedId(poolID)
      toast({ title: 'Copied', description: 'Pool ID copied to clipboard' })
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      toast({ title: 'Error', description: 'Failed to copy to clipboard', variant: 'destructive' })
    }
  }

  const handleToggleFavorite = (e: React.MouseEvent, poolID: string) => {
    e.preventDefault()
    e.stopPropagation()
    toggleFavorite(poolID)
  }

  // ── filtering logic (AND across columns, OR within each column) ───────────
  const filteredPools = pools
    .filter((pool) => {
      const query = searchQuery.toLowerCase().trim()
      if (!query) return true
      const purpose = (pool as any).controlConfig?.purpose || ''
      const purposeDisplay = purpose.replace(/_/g, ' ').toLowerCase()
      const displayName = incognito.isIncognito ? obfuscatePoolName(pool.name) : pool.name
      const displayPoolID = incognito.isIncognito ? obfuscateUUID(pool.poolID) : pool.poolID
      return (
        pool.name.toLowerCase().includes(query) ||
        pool.poolID.toLowerCase().includes(query) ||
        pool.accountID.toLowerCase().includes(query) ||
        purpose.toLowerCase().includes(query) ||
        purposeDisplay.includes(query) ||
        displayName.toLowerCase().includes(query) ||
        displayPoolID.toLowerCase().includes(query)
      )
    })
    .filter((pool) => {
      // Forecast column: pool must have at least one of the selected forecast purposes
      if (forecastFilters.size === 0) return true
      const forecasts = (pool as any).forecastConfigs as { purpose: string }[] | undefined
      return [...forecastFilters].some((fp) => forecasts?.some((f) => f.purpose === fp))
    })
    .filter((pool) => {
      // Control column: pool must have a controlConfig
      if (!controlEnabled) return true
      return (pool as any).controlConfig !== null && (pool as any).controlConfig !== undefined
    })
    .filter((pool) => {
      // Control sub-purposes: AND with control, OR within sub-purposes
      if (!controlEnabled || purposeFilters.size === 0) return true
      const p = (pool as any).controlConfig?.purpose
      return purposeFilters.has(p)
    })
    .sort((a, b) => {
      const aIsFav = isFavorite(a.poolID)
      const bIsFav = isFavorite(b.poolID)
      if (aIsFav && !bIsFav) return -1
      if (!aIsFav && bIsFav) return 1

      if (forecastFilters.size > 0) {
        const hasActiveConfig = (pool: (typeof pools)[0]) => {
          const forecasts = (pool as any).forecastConfigs as
            | { purpose: string; isActive: boolean }[]
            | undefined
          return [...forecastFilters].some((fp) =>
            forecasts?.some((f) => f.purpose === fp && f.isActive)
          )
        }
        const aActive = hasActiveConfig(a)
        const bActive = hasActiveConfig(b)
        if (aActive && !bActive) return -1
        if (!aActive && bActive) return 1
      }

      return 0
    })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">XENON Flex Demo</h1>
          <p className="text-muted-foreground">
            Flexibility pool management and control for distributed energy assets
          </p>
        </div>
        <AuthorizedButton authorized={can('PoolsWrite')} onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Pool
        </AuthorizedButton>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accounts</CardTitle>
            <AccountIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : accountCount}</div>
            <p className="text-xs text-muted-foreground">Unique accounts with pools</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pools</CardTitle>
            <PoolIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : poolCount}</div>
            <p className="text-xs text-muted-foreground">Active flexibility pools</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Systems</CardTitle>
            <SystemIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : systemCount}</div>
            <p className="text-xs text-muted-foreground">System memberships across all pools</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filter bar */}
      {!loading && pools.length > 0 && (
        <div className="flex gap-2">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter pools by name, ID, purpose..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-10 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Filter button + floating panel */}
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className={`inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors ${
                activeFilterCount > 0
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15'
                  : 'border-input bg-background text-muted-foreground hover:border-foreground/50 hover:text-foreground'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Floating dropdown */}
            {filterOpen && (
              <div className="absolute right-0 top-12 z-50 w-[480px] rounded-lg border border-border bg-popover shadow-lg">
                <div className="grid grid-cols-2 divide-x divide-border">

                  {/* ── Left column: Forecast Purposes ── */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Forecast Purposes
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setForecastFilters(new Set(activeForecastOptions.map((o) => o.value)))}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          All
                        </button>
                        <span className="text-muted-foreground/40">·</span>
                        <button
                          type="button"
                          onClick={() => setForecastFilters(new Set())}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {activeForecastOptions.map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={forecastFilters.has(value)}
                            onChange={() => toggleForecast(value)}
                            className="h-3.5 w-3.5 rounded"
                          />
                          <span className="flex-1 text-sm group-hover:text-foreground transition-colors">
                            {label}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {forecastCounts[value] ?? 0}
                          </span>
                        </label>
                      ))}
                      {activeForecastOptions.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No forecast pools</p>
                      )}
                    </div>
                  </div>

                  {/* ── Right column: Control ── */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Control
                      </p>
                    </div>

                    {/* Control master toggle */}
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={controlEnabled}
                        onChange={() => setControlEnabled((v) => !v)}
                        className="h-3.5 w-3.5 rounded"
                      />
                      <span className="flex-1 text-sm font-medium group-hover:text-foreground transition-colors">
                        Control
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {controlCount}
                      </span>
                    </label>

                    {/* Sub-purposes */}
                    <div className={`space-y-2 border-t border-border pt-3 transition-opacity ${controlEnabled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-muted-foreground">Control Purpose</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setPurposeFilters(new Set(activePurposeOptions.map((o) => o.value)))}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            All
                          </button>
                          <span className="text-muted-foreground/40">·</span>
                          <button
                            type="button"
                            onClick={() => setPurposeFilters(new Set())}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      {activePurposeOptions.map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-2.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={purposeFilters.has(value)}
                            onChange={() => togglePurpose(value)}
                            className="h-3.5 w-3.5 rounded"
                          />
                          <span className="flex-1 text-sm group-hover:text-foreground transition-colors">
                            {label}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {purposeCounts[value] ?? 0}
                          </span>
                        </label>
                      ))}
                      {activePurposeOptions.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No control pools</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={groupByAccount}
                      onChange={() => setGroupByAccount((v) => !v)}
                      className="h-3.5 w-3.5 rounded"
                    />
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      Group by Account
                    </span>
                  </label>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty State */}
      {!loading && pools.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Group className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No pools yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first flexibility pool to get started
            </p>
            <AuthorizedButton authorized={can('PoolsWrite')} onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Pool
            </AuthorizedButton>
          </CardContent>
        </Card>
      )}

      {/* No Results State */}
      {!loading && pools.length > 0 && filteredPools.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No pools found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery
                ? `No pools match "${searchQuery}"`
                : 'No pools match the selected filters.'}
            </p>
            <div className="flex gap-2">
              {searchQuery && (
                <Button variant="outline" onClick={() => setSearchQuery('')}>Clear Search</Button>
              )}
              {activeFilterCount > 0 && (
                <Button variant="outline" onClick={clearAllFilters}>Clear Filters</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pools Grid */}
      {!loading && filteredPools.length > 0 && (() => {
        const renderPoolCard = (pool: (typeof filteredPools)[0]) => (
          <Link key={pool.poolID} to={`/pools/${pool.poolID}`}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 truncate">
                    {(() => {
                      const PoolIcon = getPoolIcon(pool)
                      return <PoolIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    })()}
                    <span className="truncate">{incognito.poolName(pool.name)}</span>
                    {(pool as any).controlConfig?.dryRun && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 border border-amber-200"
                        title="Dry Run Mode - Commands are logged but not executed"
                      >
                        <TestTube className="h-3 w-3" />
                        Dry Run
                      </span>
                    )}
                    {(pool as any).isDefault && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-900 border border-emerald-200">
                        <Crown className="h-3 w-3" />
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => handleToggleFavorite(e, pool.poolID)}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      title={isFavorite(pool.poolID) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Star
                        className={`h-4 w-4 transition-colors ${
                          isFavorite(pool.poolID)
                            ? 'fill-yellow-500 text-yellow-500'
                            : 'text-muted-foreground hover:text-yellow-500'
                        }`}
                      />
                    </button>
                  </div>
                </CardTitle>
                <CardDescription className="font-mono text-xs flex items-center gap-2">
                  <span className="truncate">{incognito.uuid(pool.poolID)}</span>
                  {!incognito.isIncognito && (
                    <button
                      onClick={(e) => handleCopyPoolId(e, pool.poolID)}
                      className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                      title="Copy Pool ID"
                    >
                      {copiedId === pool.poolID ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      )}
                    </button>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <span className="text-muted-foreground flex-shrink-0">Account:</span>
                    <span className="font-mono text-xs truncate">{incognito.uuid(pool.accountID)}</span>
                  </div>
                  {(pool as any).controlConfig?.purpose && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Purpose:</span>
                      <span className="font-medium capitalize">
                        {(pool as any).controlConfig.purpose.replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  {(pool as any).forecastConfigs?.length > 0 && (
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground shrink-0">Forecast:</span>
                      <span className="flex flex-wrap gap-1 justify-end">
                        {(pool as any).forecastConfigs.map((f: { purpose: string; isActive: boolean }) => (
                          <span
                            key={f.purpose}
                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                              f.isActive ? 'text-foreground' : 'text-muted-foreground'
                            }`}
                          >
                            <span
                              className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                                f.isActive ? 'bg-blue-500' : 'bg-muted-foreground/40'
                              }`}
                            />
                            {forecastPurposeLabel(f.purpose)}
                            {!f.isActive && <span className="opacity-60">(off)</span>}
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span className="font-medium">{formatDate((pool as any).createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Systems:</span>
                    <span className="font-medium">{(pool as any).systemCount ?? 0}</span>
                  </div>
                  {pool.tags && Object.keys(pool.tags).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(
                        incognito.isIncognito
                          ? obfuscateTags(pool.tags as Record<string, string>)
                          : pool.tags
                      ).slice(0, 3).map(([key, value]) => (
                        <span
                          key={key}
                          className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-xs"
                        >
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </Link>
        )

        return groupByAccount ? (
          [...new Set(filteredPools.map((p) => p.accountID))].map((accountID) => {
            const accountPools = filteredPools.filter((p) => p.accountID === accountID)
            const isCollapsed = collapsedAccounts.has(accountID)
            const toggleCollapse = () =>
              setCollapsedAccounts((prev) => {
                const next = new Set(prev)
                if (isCollapsed) next.delete(accountID)
                else next.add(accountID)
                return next
              })
            return (
              <div key={accountID}>
                <button
                  onClick={toggleCollapse}
                  className="flex items-center gap-2 mb-3 w-full text-left hover:opacity-70 transition-opacity"
                >
                  {isCollapsed
                    ? <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    : <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  }
                  {!incognito.isIncognito && accounts[accountID]?.customization?.logo
                    ? <img src={accounts[accountID]!.customization!.logo!} alt="" className="h-6 w-auto max-w-[64px] object-contain flex-shrink-0" />
                    : <Users className="h-5 w-5 text-muted-foreground" />
                  }
                  <span className="text-base font-medium">
                    {incognito.isIncognito
                      ? incognito.accountName(accountID)
                      : (accounts[accountID]?.name ?? incognito.uuid(accountID))}
                  </span>
                  {!incognito.isIncognito && accounts[accountID]?.name && (
                    <span className="text-sm font-mono text-muted-foreground">{incognito.uuid(accountID)}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    ({accountPools.length} pool{accountPools.length !== 1 ? 's' : ''})
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
                    {accountPools.map(renderPoolCard)}
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredPools.map(renderPoolCard)}
          </div>
        )
      })()}

      {/* Create Pool Dialog */}
      <CreatePoolDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} />
    </div>
  )
}
